
import { getDriverLocations, getDriverOnDay } from '@/api';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { mapState } from 'vuex';
import store from '@/store';
import * as Sentry from '@sentry/vue';

import { LMap, LTileLayer, LMarker } from 'vue2-leaflet';
import 'leaflet.markercluster';
import { DriverLocations, DriverLocationsParams, Location as LocationType, Package } from '@/models/location';
import L, { CircleMarker, Icon } from 'leaflet';
import { GIcon } from './Lglyph';

import BackButton from '@/components/UI/backButton/BackButton.vue';
import DatePicker from '@/views/Location/DatePicker.vue';
import DriverByDayLookup from '@/views/Location/DriverByDayLookup.vue';

import { format, isValid } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import _isEqual from 'lodash/isEqual';
import * as routeNames from '@/router/routeNames';
import { timeZoneDefault } from '@/utils/PortalConstants';

import { User } from '@/models/main';

type D = Icon.Default & {
  _getIconUrl?: string;
};

@Component({
  components: {
    BackButton,
    DatePicker,
    DriverByDayLookup,
    LMap,
    LTileLayer,
    LMarker,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class Location extends Vue {
  private user!: User;

  public mapRef: HTMLFormElement;
  public mapAus;

  public loadingMap = false;
  public loadingDrivers = false;

  public date = '';
  public driversInQuery: number[] = [];
  public driversOnDate: number[] = [];
  public driversToShow: number[] = [];

  private driverMarkersAll: CircleMarker[] = [];
  // Geo center of Australia
  public center = L.latLng(-23.116667, 132.133333);
  public url = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
  public attribution =
    '<span class="ml-1">Map data © <a href="http://openstreetmap.org" class="mx-0.5">OpenStreetMap</a> contributors</span>';
  public mapIsHovered = false;
  public ctrlBtnIsDown = false;

  public mouseover(): void {
    this.mapIsHovered = true;
    document.addEventListener('keydown', this.handleCtrl);
    document.addEventListener('keyup', this.handleCtrlUp);
  }

  public mouseleave(): void {
    this.mapIsHovered = false;
    document.removeEventListener('keydown', this.handleCtrl);
    document.removeEventListener('keyup', this.handleCtrlUp);
  }

  public handleCtrl(value): void {
    if (this.mapIsHovered && value.keyCode === 17) {
      this.mapAus.scrollWheelZoom.enable();
      this.ctrlBtnIsDown = true;
    }
  }

  public handleCtrlUp(): void {
    this.mapAus.scrollWheelZoom.disable();
    this.ctrlBtnIsDown = false;
  }

  public getLocations(): void {
    const driver_code = this.driversToShow.length === this.driversOnDate.length ? 'all' : String(this.driversToShow);
    const newRoute = {
      date: this.date,
      driver_code: driver_code,
    };
    const isEqual = _isEqual(newRoute, this.$route.query);
    if (!isEqual) {
      this.$router.push({
        path: this.$route.path,
        query: { ...this.$route.query, ...newRoute },
      });
    } else {
      this.drawMapData(this.mapAus);
    }
  }

  public async changeDate(value: string): Promise<void> {
    this.date = value;
    this.clearMapLayers(this.mapAus);
    this.mapAus.flyTo(this.center, 5);
    await this.getDriversOnDate(this.date);
  }

  public changeDrivers(value: number[]): void {
    this.driversToShow = value;
  }

  private getDriversOnDate(date: string) {
    this.loadingDrivers = true;
    return getDriverOnDay(date)
      .then(data => {
        this.driversOnDate = data;
      })
      .catch(err => {
        Sentry.withScope(scope => {
          scope.setLevel('error');
          Sentry.captureException(err);
        });
      })
      .finally(() => (this.loadingDrivers = false));
  }

  public async initMapOnReady(): Promise<void> {
    delete (Icon.Default.prototype as D)._getIconUrl;
    Icon.Default.mergeOptions({
      iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
      iconUrl: require('leaflet/dist/images/marker-icon.png'),
      shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
    });
    this.mapRef = this.$refs.myMap as HTMLFormElement;
    this.mapAus = this.mapRef.mapObject;
    this.mapAus.scrollWheelZoom.disable();
    // Flag replacement:
    this.mapAus.attributionControl.setPrefix(
      `<span class="mt-1 mr-1">
        <?xml version="1.0" encoding="UTF-8"?>
        <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="20px" height="10px" viewBox="0 0 20 10" version="1.1">
          <g id="surface1">
          <path style=" stroke:none;fill-rule:nonzero;fill:rgb(69.411765%,12.941176%,17.254902%);fill-opacity:1;" d="M 19.960938 0.0976562 L 15.671875 0.0976562 L 10.628906 5.003906 L 15.671875 9.910156 L 19.960938 9.910156 L 14.917969 5.003906 L 19.960938 0.0976562 "/>
          <path style=" stroke:none;fill-rule:nonzero;fill:rgb(9.411765%,13.333333%,25.882353%);fill-opacity:1;" d="M 13.664062 0.0820312 L 9.375 0.0820312 L 3.5 5.578125 L 3.5 0.0820312 L 0.113281 0.0820312 L 0.113281 9.921875 L 3.5 9.921875 L 6.457031 7.058594 L 9.375 9.898438 L 13.664062 9.898438 L 8.621094 4.992188 L 13.664062 0.0820312 "/>
          </g>
        </svg>
      </span>`,
    );
  }

  private generateDriversByState(response: DriverLocations[]) {
    const drivers = store.getters['moduleStaticData/getDrivers'];
    const driversByState = drivers.reduce((acc, driver) => {
      if (!acc[driver.state]) {
        acc[driver.state] = [];
      }
      const driverData = response[driver.code];
      if (driverData) {
        acc[driver.state].push(driver.code);
      }
      return acc;
    }, {});
    // Remove empty arrays from the output object
    Object.keys(driversByState).forEach(key => {
      if (driversByState[key].length === 0) {
        delete driversByState[key];
      }
    });
    return driversByState;
  }

  private clearMapLayers(map): void {
    // Removing all layers except the map itself
    map.eachLayer(layer => {
      if (!layer['_tiles']) {
        layer.remove();
      }
    });
  }

  private drawLocations(driversByState, map, response: DriverLocations[]) {
    this.clearMapLayers(map);

    const colorPallet = [
      '#fb6313',
      '#663de7',
      '#5f234a',
      '#f2c653',
      '#c36cc0',
      '#05b112',
      '#537237',
      '#20f6da',
      '#9f3f1a',
      '#0fa3e6',
      '#fbb3bb',
      '#5c59d7',
    ];
    const firstGlyphIcon = GIcon.glyph({
      prefix: 'mdi',
      glyph: 'flag',
      glyphSize: '16px',
      glyphColor: 'green',
    });

    for (const austate of Object.keys(driversByState) as string[]) {
      for (const driver_number of driversByState[austate] as number[]) {
        const driverData = response[driver_number];

        if (!driverData.locations[0]) {
          continue;
        }

        const driverIdxInState = driversByState[austate].indexOf(driver_number);
        const driverColor: string = colorPallet[driverIdxInState];

        // Draw driver markers. Black circle on path line.
        // We filter out not marked locations(mark==false) and first and last elements
        // as we don't want to draw them on this step
        // If location doesn't have latitude or longitude, we don't draw it
        const driverMarkers = driverData.locations
          .filter(
            (location: LocationType, index: number) =>
              (location.mark && index !== 0 && index !== driverData.locations.length - 1) ||
              !location.latitude ||
              !location.longitude,
          )
          .map((location: LocationType) => {
            const marker = L.circleMarker([location.latitude, location.longitude], {
              color: 'black',
              fillColor: '#808080',
              fillOpacity: 0.0,
              radius: 2,
              weight: 5,
            });
            const userTimeZone = this.user.timezone || timeZoneDefault;
            marker.bindPopup(
              `Driver: #${location.driver_code.toString()}<br/>${format(
                utcToZonedTime(new Date(location.timestamp), userTimeZone),
                'yyyy-MM-dd hh:mm aa',
              )}`,
            );
            return marker;
          });

        L.featureGroup(driverMarkers).addTo(map);

        // Collecting markers to fitBounds the map
        const driverMarkersFiltred = driverData.locations.map((location: LocationType) => {
          const marker = L.circleMarker([location.latitude, location.longitude]);
          return marker;
        });
        this.driverMarkersAll = [...this.driverMarkersAll, ...driverMarkersFiltred];

        // Draw polyline on map
        L.polyline(
          driverData.locations.map((location: LocationType) => [location.latitude, location.longitude]),
          {
            color: driverColor,
            interactive: false,
            weight: 3,
          },
        ).addTo(map);

        // Draw first and last markers of driver
        const lastDivIcon = new L.DivIcon({
          className: 'last-icon',
          html: `<div class="w-full h-full p-3" style="background-color: ${driverColor};">${driver_number}</div>`,
        });

        const firstMarker = L.marker([driverData.locations[0].latitude, driverData.locations[0].longitude], {
          icon: firstGlyphIcon,
          draggable: false,
        });
        const lastMarker = L.marker(
          [
            driverData.locations[driverData.locations.length - 1].latitude,
            driverData.locations[driverData.locations.length - 1].longitude,
          ],
          {
            icon: lastDivIcon,
            draggable: false,
          },
        );
        firstMarker.bindPopup(`${driver_number} start point`);
        lastMarker.bindPopup('latest gps point');
        L.featureGroup([firstMarker, lastMarker]).addTo(map);
      }
    }
  }

  private drawPackages(map, response: DriverLocations[]) {
    const pickupGlyphIcon = GIcon.glyph({
      prefix: 'mdi',
      glyph: 'mdi-alpha-d-circle',
      glyphSize: '16px',
      glyphColor: 'white',
    });
    const deliveryGlyphIcon = GIcon.glyph({
      prefix: 'mdi',
      glyph: 'mdi-alpha-p-circle',
      glyphSize: '16px',
      glyphColor: 'white',
    });

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const markerG = L.markerClusterGroup({
      maxClusterRadius: 120,
      spiderfyOnMaxZoom: true,
    });
    for (const key of Object.keys(response)) {
      const driverPackages = response[key].packages
        .filter((pack: Package) => pack.point && pack.point.length > 0)
        .map((pack: Package) => {
          const marker = L.marker([Number(pack.point[0]), Number(pack.point[1])], {
            icon: pack.stop_type === 'P' ? pickupGlyphIcon : deliveryGlyphIcon,
            draggable: false,
          });
          const toRender = `Drv: ${key}; Job: ${pack.rockhopper_id}; No: ${pack.stop_number}; Type: ${pack.stop_type}
          <br />
          Stop name: ${pack.name};
          <br />
          Stop addres: ${pack.address_line_2}, ${pack.address_line_1}, ${pack.post_code}, ${pack.suburb}, ${pack.state}
          <br />
          Stop departed: ${pack.departed_at}
          `;
          marker.bindPopup(toRender);
          return marker;
        });
      // Collecting markers to fitBounds the map
      this.driverMarkersAll = [...this.driverMarkersAll, ...driverPackages];

      markerG.addLayers(driverPackages);
      map.addLayer(markerG);
    }
  }

  public async drawMapData(map) {
    this.loadingMap = true;
    const driver_code = this.driversToShow.length === this.driversOnDate.length ? 'all' : String(this.driversToShow);
    const params: DriverLocationsParams = {
      driver_code: driver_code,
      date: this.date,
    };
    // Draw driver locations
    const response = await getDriverLocations(params);
    const driversByState = this.generateDriversByState(response);
    // Draw locations, first and last markers, polylines
    this.drawLocations(driversByState, map, response);
    // Draw packages
    this.drawPackages(map, response);
    // Setting center and zoom
    if (this.driverMarkersAll.length >= 1) {
      const allDriversGroup = L.featureGroup(this.driverMarkersAll);
      map.fitBounds(allDriversGroup.getBounds());
    }
    this.loadingMap = false;
  }

  @Watch('$route.query', { immediate: true })
  private async routeChanged(): Promise<void> {
    if (this.$route.name === routeNames.LOCATIONS) {
      // Date: get from the query if valid or set today
      if (this.$route.query?.date && isValid(new Date(String(this.$route.query.date)))) {
        this.date = String(this.$route.query.date);
      } else {
        const userTimeZone = this.user.timezone || timeZoneDefault;
        this.date = format(utcToZonedTime(new Date(), userTimeZone), 'yyyy-MM-dd');
      }
    }
    await this.getDriversOnDate(this.date);

    // Drivers:
    if (this.$route.query?.driver_code) {
      const driver_code = this.$route.query.driver_code as string;
      if (driver_code.toLowerCase() === 'all') {
        this.driversToShow = this.driversOnDate;
      } else {
        this.driversInQuery = driver_code.split(',').map(code => Number(code));
        this.driversToShow = this.driversOnDate.filter((element: number) => this.driversInQuery.includes(element));
      }
    }

    if (!!this.date && !!this.driversToShow.length) {
      this.drawMapData(this.mapAus);
    }
  }
}
